import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faServer,
  faDollarSign,
  faHeartbeat,
  faChartLine,
  faBell,
  faCloud,
  faRefresh,
  faEye,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faGithub } from '@fortawesome/free-brands-svg-icons';
import { useInfrastructureData } from './hooks/useInfrastructureData';
import { SummaryCard, ServiceSection, ResourceTable, ResourceFilter, ResourceDetailsModal, ResourceOverview, CloudWatchAlertsModal } from './components';
import { resourceDetailsService } from './services/resourceDetailsService';
import { cloudWatchService } from './services/cloudWatchService';
import type { FilterOptions } from './components/ResourceFilter';

export const InfrastructureTab: React.FC = () => {
  console.log('InfrastructureTab: Component rendered at', new Date().toISOString());

  const { data, loading, error, refetch, lastUpdated } = useInfrastructureData({
    autoRefresh: true, // Re-enabled after fixing infinite loop
    refreshInterval: 60000 // 60 seconds
  });

  // Fetch CloudWatch alerts count - Re-enabled after fixing infinite loop
  React.useEffect(() => {
    const fetchAlertsCount = async () => {
      try {
        const alertData = await cloudWatchService.getAllAlerts();
        setAlertsCount(alertData.summary.total);
      } catch (error) {
        console.error('Failed to fetch alerts count:', error);
        setAlertsCount(0);
      }
    };

    if (data) {
      fetchAlertsCount();
    }
  }, [data]);

  // State for filtering and modal
  const [filters, setFilters] = useState<FilterOptions>({
    accounts: [],
    regions: [],
    resourceTypes: [],
    costRange: { min: 0, max: Infinity }
  });

  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>({
    accounts: [],
    regions: [],
    resourceTypes: [],
    costRange: { min: 0, max: Infinity }
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    resourceType: 'ec2' | 'rds' | 's3' | 'lambda' | 'eks' | null;
    accountId?: string;
  }>({
    isOpen: false,
    resourceType: null,
    accountId: undefined
  });

  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [alertsCount, setAlertsCount] = useState(0);
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Pre-fill all filters when data loads — so all checkboxes are ticked by default
  React.useEffect(() => {
    if (data && !filtersInitialized) {
      const allAccounts = [
        ...data.accounts.aws,
        ...data.accounts.gcp,
        ...data.accounts.github
      ].map((acc: any) => acc.id);

      const allRegions = Array.from(new Set([
        ...data.aws.ec2.instances.map((i: any) => i.region),
        ...data.aws.rds.instances.map((i: any) => i.region),
        ...data.aws.s3.buckets.map((b: any) => b.region),
        ...data.aws.lambda.functions.map((f: any) => f.region),
        ...data.aws.eks.clusters.map((c: any) => c.region)
      ].filter(Boolean))) as string[];

      const allResourceTypes = ['EC2', 'RDS', 'S3', 'Lambda', 'EKS'];

      const defaultFilters: FilterOptions = {
        accounts: allAccounts,
        regions: allRegions,
        resourceTypes: allResourceTypes,
        costRange: { min: 0, max: Infinity }
      };

      setFilters(defaultFilters);
      setAppliedFilters(defaultFilters);
      setFiltersInitialized(true);
      console.log('Infrastructure Tab: Filters pre-filled with all options', { accounts: allAccounts.length, regions: allRegions.length, resourceTypes: allResourceTypes.length });
    }
  }, [data, filtersInitialized]);

  console.log('InfrastructureTab: Hook state', {
    hasData: !!data,
    loading,
    hasError: !!error,
    lastUpdated,
    dataPreview: data ? {
      totalResources: data.summary?.totalResources,
      awsEc2Total: data.aws?.ec2?.total,
      awsRdsTotal: data.aws?.rds?.total
    } : null
  });

  const handleRefresh = async () => {
    console.log('Manual refresh triggered');
    await refetch();
  };

  // Handler for opening resource details modal
  const handleResourceClick = useCallback((resourceType: 'ec2' | 'rds' | 's3' | 'lambda' | 'eks', accountId?: string) => {
    setModalState({
      isOpen: true,
      resourceType,
      accountId
    });
  }, []);

  // Handler for closing modal
  const handleCloseModal = useCallback(() => {
    setModalState({
      isOpen: false,
      resourceType: null,
      accountId: undefined
    });
  }, []);

  // Handler for fetching resource details — use filtered data (respects account/region filters)
  const handleFetchResourceDetails = useCallback(async (resourceType: string, accountId?: string) => {
    const filtered = getFilteredData();
    console.log(`Fetching details for ${resourceType}`, { accountId, hasData: !!filtered });

    if (filtered) {
      switch (resourceType) {
        case 'ec2': return filtered.aws?.ec2?.instances || [];
        case 'rds': return filtered.aws?.rds?.instances || [];
        case 's3': return filtered.aws?.s3?.buckets || [];
        case 'lambda': return filtered.aws?.lambda?.functions || [];
        case 'eks': return filtered.aws?.eks?.clusters || [];
        default: return [];
      }
    }

    // Fallback to API call if data not loaded yet
    return await resourceDetailsService.getResourceDetails(resourceType, accountId);
  }, [data, appliedFilters]);

  // Filter handlers
  const handleFilterChange = useCallback((newFilters: FilterOptions) => {
    setFilters(newFilters);
    console.log('Filters updated:', newFilters);
  }, []);

  const handleApplyFilters = useCallback((newFilters: FilterOptions) => {
    setAppliedFilters(newFilters);
    console.log('Filters applied:', newFilters);
    // No refetch needed — filtering is client-side on already loaded data
  }, []);

  // Filter data based on applied filters
  const getFilteredData = useCallback(() => {
    if (!data) return null;

    // If no filters applied OR all filters selected, return original data
    const availableOptions = getAvailableFilterOptions();
    const allAccountsSelected = appliedFilters.accounts.length === 0 || appliedFilters.accounts.length === availableOptions.accounts.length;
    const allRegionsSelected = appliedFilters.regions.length === 0 || appliedFilters.regions.length === availableOptions.regions.length;
    const allTypesSelected = appliedFilters.resourceTypes.length === 0 || appliedFilters.resourceTypes.length === availableOptions.resourceTypes.length;

    if (allAccountsSelected && allRegionsSelected && allTypesSelected) {
      return data;
    }

    const filteredData = JSON.parse(JSON.stringify(data)); // Deep clone to avoid mutating original

    // Filter by accounts
    if (appliedFilters.accounts.length > 0) {
      // Get selected account names from IDs
      const selectedAccountNames = data.accounts.aws
        .filter((acc: any) => appliedFilters.accounts.includes(acc.id))
        .map((acc: any) => acc.name);

      filteredData.aws.ec2.instances = filteredData.aws.ec2.instances.filter((i: any) => selectedAccountNames.includes(i.accountName));
      filteredData.aws.rds.instances = filteredData.aws.rds.instances.filter((i: any) => selectedAccountNames.includes(i.accountName));
      filteredData.aws.s3.buckets = filteredData.aws.s3.buckets.filter((b: any) => selectedAccountNames.includes(b.accountName));
      filteredData.aws.lambda.functions = filteredData.aws.lambda.functions.filter((f: any) => selectedAccountNames.includes(f.accountName));
      filteredData.aws.eks.clusters = filteredData.aws.eks.clusters.filter((c: any) => selectedAccountNames.includes(c.accountName));

      // Recalculate totals
      filteredData.aws.ec2.total = filteredData.aws.ec2.instances.length;
      filteredData.aws.ec2.running = filteredData.aws.ec2.instances.filter((i: any) => i.status === 'running').length;
      filteredData.aws.ec2.stopped = filteredData.aws.ec2.instances.filter((i: any) => i.status === 'stopped').length;
      filteredData.aws.rds.total = filteredData.aws.rds.instances.length;
      filteredData.aws.rds.available = filteredData.aws.rds.instances.filter((i: any) => i.status === 'available').length;
      filteredData.aws.lambda.total = filteredData.aws.lambda.functions.length;
      filteredData.aws.lambda.active = filteredData.aws.lambda.functions.filter((f: any) => f.status === 'active' || f.status === 'available').length;
      filteredData.aws.eks.total = filteredData.aws.eks.clusters.length;
      filteredData.aws.eks.active = filteredData.aws.eks.clusters.filter((c: any) => c.status === 'active').length;

      // Recalculate cost for selected accounts only
      if (filteredData.aws.cost?.byAccount) {
        filteredData.aws.cost.total = selectedAccountNames.reduce((sum: number, name: string) => {
          return sum + (filteredData.aws.cost.byAccount[name] || 0);
        }, 0);
        // Rebuild byService for selected accounts only
        const filteredByService: any = {};
        selectedAccountNames.forEach((name: string) => {
          const accountServices = filteredData.aws.cost.byServicePerAccount?.[name] || {};
          Object.entries(accountServices).forEach(([service, cost]) => {
            filteredByService[service] = (filteredByService[service] || 0) + (cost as number);
          });
        });
        filteredData.aws.cost.byService = filteredByService;
      }
    }

    // Filter AWS data based on applied filters
    if (appliedFilters.resourceTypes.length > 0) {
      if (!appliedFilters.resourceTypes.includes('EC2')) {
        filteredData.aws.ec2.instances = [];
        filteredData.aws.ec2.total = 0;
        filteredData.aws.ec2.running = 0;
      }
      if (!appliedFilters.resourceTypes.includes('RDS')) {
        filteredData.aws.rds.instances = [];
        filteredData.aws.rds.total = 0;
        filteredData.aws.rds.available = 0;
      }
      if (!appliedFilters.resourceTypes.includes('S3')) {
        filteredData.aws.s3.buckets = [];
      }
      if (!appliedFilters.resourceTypes.includes('Lambda')) {
        filteredData.aws.lambda.functions = [];
        filteredData.aws.lambda.total = 0;
        filteredData.aws.lambda.active = 0;
      }
      if (!appliedFilters.resourceTypes.includes('EKS')) {
        filteredData.aws.eks.clusters = [];
        filteredData.aws.eks.total = 0;
        filteredData.aws.eks.active = 0;
      }
    }

    // Filter by regions
    if (appliedFilters.regions.length > 0) {
      filteredData.aws.ec2.instances = filteredData.aws.ec2.instances.filter((i: any) => appliedFilters.regions.includes(i.region));
      filteredData.aws.rds.instances = filteredData.aws.rds.instances.filter((i: any) => appliedFilters.regions.includes(i.region));
      filteredData.aws.s3.buckets = filteredData.aws.s3.buckets.filter((b: any) => appliedFilters.regions.includes(b.region));
      filteredData.aws.lambda.functions = filteredData.aws.lambda.functions.filter((f: any) => appliedFilters.regions.includes(f.region));
      filteredData.aws.eks.clusters = filteredData.aws.eks.clusters.filter((c: any) => appliedFilters.regions.includes(c.region));

      // Recalculate totals
      filteredData.aws.ec2.total = filteredData.aws.ec2.instances.length;
      filteredData.aws.ec2.running = filteredData.aws.ec2.instances.filter((i: any) => i.status === 'running').length;
      filteredData.aws.rds.total = filteredData.aws.rds.instances.length;
      filteredData.aws.rds.available = filteredData.aws.rds.instances.filter((i: any) => i.status === 'available').length;
      filteredData.aws.lambda.total = filteredData.aws.lambda.functions.length;
      filteredData.aws.lambda.active = filteredData.aws.lambda.functions.filter((f: any) => f.status === 'active').length;
      filteredData.aws.eks.total = filteredData.aws.eks.clusters.length;
      filteredData.aws.eks.active = filteredData.aws.eks.clusters.filter((c: any) => c.status === 'active').length;
    }

    // Recalculate summary totals
    filteredData.summary.totalResources =
      filteredData.aws.ec2.total +
      filteredData.aws.rds.total +
      filteredData.aws.s3.buckets.length +
      filteredData.aws.lambda.total +
      filteredData.aws.eks.total;
    filteredData.summary.totalCost = filteredData.aws.cost?.total || 0;

    return filteredData;
  }, [data, appliedFilters]);

  // Get available filter options from data
  const getAvailableFilterOptions = useCallback(() => {
    if (!data) return { accounts: [], regions: [], resourceTypes: [] };

    const accounts = [
      ...data.accounts.aws,
      ...data.accounts.gcp,
      ...data.accounts.github
    ];

    const regions = Array.from(new Set([
      ...data.aws.ec2.instances.map(i => i.region),
      ...data.aws.rds.instances.map(i => i.region),
      ...data.aws.s3.buckets.map(b => b.region),
      ...data.aws.lambda.functions.map(f => f.region),
      ...data.aws.eks.clusters.map(c => c.region)
    ].filter(Boolean)));

    const resourceTypes = ['EC2', 'RDS', 'S3', 'Lambda', 'EKS'];

    return { accounts, regions, resourceTypes };
  }, [data]);

  if (error) {
    return (
      <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
            <div>
              <div className="text-xs font-bold text-red-900">
                Failed to load infrastructure data
              </div>
              <div className="text-xs text-red-700 mt-1">
                {error.message}
              </div>
            </div>
            <button
              onClick={handleRefresh}
              className="ml-auto px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-xs font-medium hover:bg-gray-50"
            >
              <FontAwesomeIcon icon={faRefresh} className="mr-1" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
      {/* Header with Status */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${loading ? 'bg-blue-400 animate-pulse' : 'bg-green-400'}`}></div>
            <div>
              <div className="text-base font-bold text-gray-900">
                Infrastructure
              </div>
              <div className="text-xs text-gray-600">
                {data ?
                  `${data.accounts.aws.length} AWS • ${data.accounts.gcp.length} GCP • ${data.accounts.github.length} GitHub` :
                  'Loading...'
                }
                {lastUpdated && (
                  <span className="ml-2">• {lastUpdated.toLocaleTimeString()}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faRefresh} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50">
              <FontAwesomeIcon icon={faEye} className="mr-1" />
              Monitor
            </button>
          </div>
        </div>
      </div>

      {/* Resource Filter */}
      {data && (
        <ResourceFilter
          filters={filters}
          appliedFilters={appliedFilters}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          availableAccounts={getAvailableFilterOptions().accounts}
          availableRegions={getAvailableFilterOptions().regions}
          availableResourceTypes={getAvailableFilterOptions().resourceTypes}
        />
      )}

      {/* Executive Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <SummaryCard
          title="Total Resources"
          value={getFilteredData()?.summary.totalResources || 0}
          icon={faServer}
          color="blue"
          loading={loading}
        />
        <div onClick={() => setCostModalOpen(true)} className="cursor-pointer">
          <SummaryCard
            title="Monthly Cost (Last Month)"
            value={getFilteredData()?.summary.totalCost ? `$${getFilteredData()!.summary.totalCost.toFixed(2)}` : '$0.00'}
            icon={faDollarSign}
            color="green"
            trend={data?.aws.cost?.trend || 'stable'}
            loading={loading}
          />
        </div>
        <SummaryCard
          title="Health Score"
          value={data?.summary.healthScore ? `${data.summary.healthScore}%` : '0%'}
          icon={faHeartbeat}
          color={data?.summary.healthScore ? (data.summary.healthScore >= 80 ? 'green' : data.summary.healthScore >= 60 ? 'yellow' : 'red') : 'gray'}
          loading={loading}
        />
        <SummaryCard
          title="Utilization"
          value={data?.summary.utilizationRate ? `${data.summary.utilizationRate}%` : '0%'}
          icon={faChartLine}
          color="purple"
          loading={loading}
        />
        <div onClick={() => setAlertsModalOpen(true)} className="cursor-pointer">
          <SummaryCard
            title="Active Alerts"
            value={alertsCount}
            icon={faBell}
            color={alertsCount > 0 ? 'red' : 'green'}
            loading={loading}
          />
        </div>
      </div>

      {/* Service Sections */}
      <div className="space-y-3">
        {/* AWS Section */}
        <ServiceSection
          title="Amazon Web Services"
          icon={faAws}
          color="orange"
          data={{}}
          loading={loading}
          onRefresh={() => {/* Could refresh just AWS data */ }}
        >
          {getFilteredData() && (
            <div className="space-y-3">
              {/* AWS Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div onClick={() => handleResourceClick('ec2', data?.accounts?.aws?.[0]?.id)} className="cursor-pointer">
                  <SummaryCard
                    title="EC2 Instances"
                    value={getFilteredData()!.aws.ec2.total}
                    icon={faServer}
                    color="blue"
                    subtitle={`${getFilteredData()!.aws.ec2.running} running`}
                  />
                </div>
                <div onClick={() => handleResourceClick('rds', data?.accounts?.aws?.[0]?.id)} className="cursor-pointer">
                  <SummaryCard
                    title="RDS Instances"
                    value={getFilteredData()!.aws.rds.total}
                    icon={faServer}
                    color="green"
                    subtitle={`${getFilteredData()!.aws.rds.available} available`}
                  />
                </div>
                <div onClick={() => handleResourceClick('s3', data?.accounts?.aws?.[0]?.id)} className="cursor-pointer">
                  <SummaryCard
                    title="S3 Buckets"
                    value={getFilteredData()!.aws.s3.buckets.length}
                    icon={faCloud}
                    color="indigo"
                    subtitle={getFilteredData()!.aws.s3.totalSize}
                  />
                </div>
                <div onClick={() => handleResourceClick('lambda', data?.accounts?.aws?.[0]?.id)} className="cursor-pointer">
                  <SummaryCard
                    title="Lambda Functions"
                    value={getFilteredData()!.aws.lambda.total}
                    icon={faServer}
                    color="yellow"
                    subtitle={`${getFilteredData()!.aws.lambda.active} active`}
                  />
                </div>
                <div onClick={() => handleResourceClick('eks', data?.accounts?.aws?.[0]?.id)} className="cursor-pointer">
                  <SummaryCard
                    title="EKS Clusters"
                    value={getFilteredData()!.aws.eks.total}
                    icon={faServer}
                    color="purple"
                    subtitle={`${getFilteredData()!.aws.eks.active} active`}
                  />
                </div>
              </div>

              {/* AWS Resource Overview */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-2">Resource Overview</h4>
                <ResourceOverview
                  data={getFilteredData()}
                  loading={loading}
                />
              </div>
            </div>
          )}
        </ServiceSection>

        {/* GCP Section — HIDDEN (uncomment to re-enable) */}
        {false && (
          <ServiceSection
            title="Google Cloud Platform"
            icon={faGoogle}
            color="blue"
            data={{}}
            loading={loading}
            onRefresh={() => { /* Could refresh just GCP data */ }}
          >
            {data && (
              <div className="space-y-3">
                {/* GCP Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <SummaryCard
                    title="GCE Instances"
                    value={data?.gcp?.gce?.total ?? 0}
                    icon={faServer}
                    color="blue"
                    subtitle={`${data?.gcp?.gce?.running ?? 0} running`}
                  />
                  <SummaryCard
                    title="GKS Clusters"
                    value={data?.gcp?.gks?.total ?? 0}
                    icon={faServer}
                    color="green"
                    subtitle={`${data?.gcp?.gks?.active ?? 0} active`}
                  />
                  <SummaryCard
                    title="BigQuery"
                    value={data?.gcp?.bigquery?.datasets ?? 0}
                    icon={faServer}
                    color="indigo"
                    subtitle={`${data?.gcp?.bigquery?.tables ?? 0} tables`}
                  />
                </div>

                {/* GCP Resources Table */}
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Recent Resources</h4>
                  <ResourceTable
                    resources={[
                      ...(data?.gcp?.gce?.instances?.slice(0, 3) ?? []),
                      ...(data?.gcp?.gks?.clusters?.slice(0, 2) ?? [])
                    ]}
                    type="gcp"
                    maxItems={5}
                  />
                </div>
              </div>
            )}
          </ServiceSection>
        )}

        {/* GitHub Section */}
        <ServiceSection
          title="GitHub"
          icon={faGithub}
          color="gray"
          data={{}}
          loading={loading}
          onRefresh={() => {/* Could refresh just GitHub data */ }}
        >
          {data && (
            <div className="space-y-3">
              {/* GitHub Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <SummaryCard
                  title="Repositories"
                  value={data.github.repositories.total}
                  icon={faServer}
                  color="purple"
                  subtitle={`${data.github.repositories.public} public`}
                />
                <SummaryCard
                  title="Workflows"
                  value={data.github.workflows.total}
                  icon={faServer}
                  color="blue"
                  subtitle={`${data.github.workflows.active} active`}
                />
                <SummaryCard
                  title="Commits"
                  value={data.github.stats.commits}
                  icon={faServer}
                  color="green"
                  subtitle="Last 7 days"
                />
                <SummaryCard
                  title="Pull Requests"
                  value={data.github.stats.pullRequests}
                  icon={faServer}
                  color="yellow"
                  subtitle="Open"
                />
              </div>

              {/* GitHub Resources Table */}
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-2">Recent Repositories</h4>
                <ResourceTable
                  resources={data.github.repositories.list.slice(0, 5)}
                  type="github"
                  maxItems={5}
                />
              </div>
            </div>
          )}
        </ServiceSection>
      </div>

      {/* Resource Details Modal */}
      {modalState.isOpen && modalState.resourceType && (
        <ResourceDetailsModal
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          resourceType={modalState.resourceType}
          accountId={modalState.accountId}
          onFetchDetails={handleFetchResourceDetails}
        />
      )}

      {/* CloudWatch Alerts Modal */}
      <CloudWatchAlertsModal
        isOpen={alertsModalOpen}
        onClose={() => setAlertsModalOpen(false)}
      />

      {/* Cost Breakdown Modal */}
      {costModalOpen && getFilteredData() && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Cost Breakdown (Last Month)</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Total: ${getFilteredData()!.summary.totalCost?.toFixed(2) || '0.00'}
                </p>
              </div>
              <button onClick={() => setCostModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              {(() => {
                const costData = getFilteredData()!.aws.cost;
                const services = Object.entries(costData.byService || {})
                  .map(([service, cost]) => ({ service, cost: cost as number }))
                  .filter(s => s.cost > 0)
                  .sort((a, b) => b.cost - a.cost);

                return (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Service</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Cost</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((s, idx) => (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-gray-900">{s.service}</td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">${s.cost.toFixed(2)}</td>
                          <td className="py-3 px-4 text-right text-gray-500">
                            {costData.total > 0 ? ((s.cost / costData.total) * 100).toFixed(1) : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-bold">
                      <tr>
                        <td className="py-3 px-4 text-gray-900">Total</td>
                        <td className="py-3 px-4 text-right text-gray-900">${costData.total?.toFixed(2) || '0.00'}</td>
                        <td className="py-3 px-4 text-right text-gray-500">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                );
              })()}

              {/* Per-account breakdown */}
              {getFilteredData()!.aws.cost?.byAccount && Object.keys(getFilteredData()!.aws.cost.byAccount).length > 1 && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Cost by Account</h3>
                  <div className="space-y-2">
                    {Object.entries(getFilteredData()!.aws.cost.byAccount)
                      .sort(([,a], [,b]) => (b as number) - (a as number))
                      .map(([account, cost], idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <span className="text-sm font-medium text-gray-900">{account}</span>
                          <span className="text-sm font-bold text-gray-900">${(cost as number).toFixed(2)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button onClick={() => setCostModalOpen(false)} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfrastructureTab;